import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Edit, 
  MoreHorizontal, 
  Grid2x2Check, 
  Activity, 
  Users, 
  Package, 
  CreditCard, 
  Building2,
  Mail,
  Phone,
  Globe,
  ArrowUpRight,
  CalendarPlus,
  Tag,
  ChevronRight,
  Eye,
  EyeOff
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@marine/ui/avatar';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@marine/ui/tabs';
import { ScrollArea } from '@marine/ui/scroll-area';
import { Separator } from '@marine/ui/separator';
import { formatPhoneUS } from '@marine/utils/formatters';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@marine/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@marine/ui/dropdown-menu';
import {
  RiCheckLine,
  RiPauseLine,
  RiDeleteBinLine,
} from '@remixicon/react';

import { useTenant, useActivateTenant, useSuspendTenant, useDeleteTenant, useTenantUsers } from '../hooks/useTenants';
import { useModules, useTenantModules, useEnableModule, useDisableModule } from '../hooks/useModules';
import { TenantFormSheet } from '../components/TenantFormSheet';
import { useBreadcrumbLeaf } from '@/components/layout/components/breadcrumb-context';
import { ProvisioningStatusBadge } from '@/components/tenants/ProvisioningStatusBadge';
import type { TenantUser } from '../types/platform.types';
import { TenantAiKeyOverridesSection } from './tenants/TenantAiKeyOverridesSection';
import { TenantAiConfigSection } from './tenants/TenantAiConfigSection';
import { TenantAiPromptsSection } from './tenants/TenantAiPromptsSection';
import { Sparkles, FileText } from 'lucide-react';

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [formDialog, setFormDialog] = useState(false);
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const usersPerPage = 10;

  const { data: tenant, isLoading, error } = useTenant(id!);
  useBreadcrumbLeaf(tenant?.name);
  const { mutate: activateTenant } = useActivateTenant();
  const { mutate: suspendTenant } = useSuspendTenant();
  const { mutate: deleteTenant } = useDeleteTenant();
  const { data: tenantUsers, isLoading: usersLoading, error: usersError } = useTenantUsers(id!, {
    page: usersPage,
    per_page: usersPerPage,
    search: usersSearch || undefined,
  });

  // Module management hooks
  const { data: allModules, isLoading: modulesLoading, error: modulesError } = useModules();
  const { data: tenantModules } = useTenantModules(id!);
  const enableModuleMutation = useEnableModule();
  const disableModuleMutation = useDisableModule();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Activity className="h-5 w-5 animate-spin" />
          </div>
          <div className="text-sm font-medium">Loading tenant</div>
          <div className="text-xs text-muted-foreground">Fetching tenant details and configuration.</div>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <Activity className="h-5 w-5" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium">Failed to load tenant details</p>
          <p className="text-xs text-muted-foreground mt-1">The tenant may not exist or there was a network error.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/platform/tenants')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tenants
        </Button>
      </div>
    );
  }

  const tenantData = tenant;
  const storageSettings = tenantData.settings?.storage as {
    provider?: string;
    disk?: string;
    bucket?: string;
    access_key?: string;
    endpoint?: string;
    region?: string;
    url?: string;
    use_path_style_endpoint?: boolean;
    secret_configured?: boolean;
    policy?: string;
    usage_bytes?: number | null;
    usage_human?: string | null;
  } | undefined;
  const initials = tenantData.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const getStatusBadge = (status: string) => {
    const variantMap: Record<string, 'primary' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'primary',
      inactive: 'secondary',
      suspended: 'destructive',
      pending_approval: 'outline',
    };
    return (
      <Badge variant={variantMap[status] || 'secondary'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getUserDisplayName = (user: TenantUser) => {
    const name = user.full_name || user.name || `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
    return name || user.email;
  };

  const getUserInitials = (user: TenantUser) => {
    return getUserDisplayName(user)
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserRoles = (user: TenantUser) => {
    if (user.roles?.length) {
      return user.roles.map((role) => role.display_name || role.name);
    }
    return user.role ? [user.role] : [];
  };

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete ${tenantData.name}? This action cannot be undone.`)) {
      deleteTenant(tenantData.id, {
        onSuccess: () => navigate('/platform/tenants'),
      });
    }
  };

  return (
    <div className="space-y-6 pb-8 p-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/platform/tenants')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="h-6 w-px bg-border" />
          <div className="flex items-center gap-2.5">
            <Avatar className="size-8 rounded-lg">
              <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-sm font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex items-center gap-2.5">
              <h1 className="text-sm font-semibold">{tenantData.name}</h1>
              {getStatusBadge(tenantData.status)}
              <ProvisioningStatusBadge status={tenantData.provisioning_status} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setFormDialog(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {tenantData.status === 'active' ? (
                <DropdownMenuItem
                  onClick={() => {
                    const reason = prompt('Enter reason for suspension:');
                    if (reason) {
                      suspendTenant({ id: tenantData.id, reason });
                    }
                  }}
                >
                  <RiPauseLine className="w-4 h-4 mr-2" />
                  Suspend Tenant
                </DropdownMenuItem>
              ) : tenantData.status === 'suspended' ? (
                <DropdownMenuItem onClick={() => activateTenant(tenantData.id)}>
                  <RiCheckLine className="w-4 h-4 mr-2" />
                  Activate Tenant
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
                <RiDeleteBinLine className="w-4 h-4 mr-2" />
                Delete Tenant
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="flex gap-6">
        {/* Main Content - Left Side */}
        <div className="flex-1 border border-border rounded-lg">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="text-sm">
            <TabsList
              variant="line"
              className="px-5 gap-6 bg-transparent [&_button]:border-b [&_button_svg]:size-4 [&_button]:text-secondary-foreground"
            >
              <TabsTrigger value="overview">
                <Grid2x2Check />
                Overview
              </TabsTrigger>
              <TabsTrigger value="activity">
                <Activity />
                Activity
              </TabsTrigger>
              <TabsTrigger value="users">
                <Users />
                Users
              </TabsTrigger>
              <TabsTrigger value="modules">
                <Package />
                Modules
              </TabsTrigger>
              <TabsTrigger value="ai-keys">
                <Sparkles />
                AI Keys
              </TabsTrigger>
              <TabsTrigger value="ai-config">
                <Sparkles />
                AI Config
              </TabsTrigger>
              <TabsTrigger value="ai-prompts">
                <FileText />
                AI Prompts
              </TabsTrigger>
              <TabsTrigger value="billing">
                <CreditCard />
                Billing
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="w-full">
              <div className="px-5 py-4">
                <TabsContent value="overview" className="space-y-6">
                  {/* Business Information */}
                  <div className="space-y-4">
                    <h3 className="ms-1 flex items-center gap-1.5 text-sm font-semibold">
                      <Building2 className="size-3.5 opacity-60" />
                      Business Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {tenantData.legal_name && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Legal Name</label>
                          <p className="mt-1 text-sm">{tenantData.legal_name}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Business Type</label>
                        <p className="mt-1 text-sm capitalize">{tenantData.business_type.replace('_', ' ')}</p>
                      </div>
                      {tenantData.tax_id && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Tax ID</label>
                          <p className="mt-1 text-sm">{tenantData.tax_id}</p>
                        </div>
                      )}
                      {tenantData.license_number && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">License Number</label>
                          <p className="mt-1 text-sm">{tenantData.license_number}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Technical Details */}
                  <div className="space-y-4">
                    <h3 className="ms-1 flex items-center gap-1.5 text-sm font-semibold">
                      <Activity className="size-3.5 opacity-60" />
                      Technical Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Tenant ID</label>
                        {/* #597 — IDs live in URLs and clipboards, never as rendered text. */}
                        <p className="mt-1">
                          <button
                            type="button"
                            className="text-sm font-medium text-link hover:underline"
                            onClick={() => {
                              void navigator.clipboard.writeText(tenantData.id).then(
                                () => toast.success('Tenant ID copied'),
                                () => toast.error('Could not copy to clipboard'),
                              );
                            }}
                          >
                            Copy ID
                          </button>
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Database</label>
                        <p className="mt-1 text-sm font-mono">{tenantData.database}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Created At</label>
                        <p className="mt-1 text-sm">{new Date(tenantData.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                        <p className="mt-1 text-sm">{new Date(tenantData.updated_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {storageSettings && (
                    <>
                      <Separator />
                      <div className="space-y-4">
                        <h3 className="ms-1 flex items-center gap-1.5 text-sm font-semibold">
                          <ChevronRight className="size-3.5 opacity-60" />
                          Storage Configuration
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Provider</label>
                            <p className="mt-1 text-sm">{storageSettings.provider || 'minio'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Disk</label>
                            <p className="mt-1 text-sm font-mono">{storageSettings.disk || 'tenant'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Bucket</label>
                            <p className="mt-1 text-sm font-mono">{storageSettings.bucket || '—'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Storage Used</label>
                            <p className="mt-1 text-sm font-mono">
                              {storageSettings.usage_human || '—'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Access Key</label>
                            <div className="mt-1 flex items-center gap-2">
                              <p className="text-sm font-mono">
                                {storageSettings.access_key
                                  ? showAccessKey
                                    ? storageSettings.access_key
                                    : `${storageSettings.access_key.slice(0, 4)}••••${storageSettings.access_key.slice(-4)}`
                                  : '—'}
                              </p>
                              {storageSettings.access_key && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setShowAccessKey((prev) => !prev)}
                                  title={showAccessKey ? 'Hide access key' : 'Show access key'}
                                >
                                  {showAccessKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Secret Key</label>
                            <p className="mt-1 text-sm">
                              {storageSettings.secret_configured ? 'Configured' : 'Not configured'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Endpoint</label>
                            <p className="mt-1 text-sm font-mono">{storageSettings.endpoint || '—'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Region</label>
                            <p className="mt-1 text-sm font-mono">{storageSettings.region || '—'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Path Style</label>
                            <p className="mt-1 text-sm">
                              {storageSettings.use_path_style_endpoint ? 'Enabled' : 'Disabled'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Public URL</label>
                            <p className="mt-1 text-sm font-mono">{storageSettings.url || '—'}</p>
                          </div>
                          {storageSettings.policy && (
                            <div>
                              <label className="text-sm font-medium text-muted-foreground">Policy</label>
                              <p className="mt-1 text-sm font-mono">{storageSettings.policy}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Notes */}
                  {tenantData.notes && (
                    <>
                      <Separator />
                      <div className="space-y-4">
                        <h3 className="ms-1 flex items-center gap-1.5 text-sm font-semibold">
                          <Edit className="size-3.5 opacity-60" />
                          Notes
                        </h3>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{tenantData.notes}</p>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="space-y-4">
                  <div className="py-12 text-center">
                    <p className="text-muted-foreground">Activity timeline coming soon...</p>
                  </div>
                </TabsContent>

                <TabsContent value="users" className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="w-full sm:max-w-xs">
                      <Input
                        placeholder="Search users..."
                        value={usersSearch}
                        onChange={(event) => {
                          setUsersSearch(event.target.value);
                          setUsersPage(1);
                        }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {tenantUsers?.meta?.total ?? 0} users
                    </div>
                  </div>

                  {usersLoading ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-12">
                      <Activity className="h-5 w-5 animate-spin text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Loading users...</p>
                    </div>
                  ) : usersError ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-12">
                      <p className="text-sm text-destructive">Failed to load tenant users</p>
                      <p className="text-xs text-muted-foreground">Please try refreshing the page.</p>
                    </div>
                  ) : tenantUsers?.items?.length ? (
                    <div className="space-y-3">
                      {tenantUsers.items.map((user) => {
                        const displayName = getUserDisplayName(user);
                        const roles = getUserRoles(user);
                        return (
                          <div
                            key={user.id}
                            className="flex flex-col gap-3 rounded-lg border border-border p-4"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <Avatar className="size-9 rounded-lg">
                                  <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                                    {getUserInitials(user)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="font-medium">{displayName}</div>
                                  <div className="text-sm text-muted-foreground">{user.email}</div>
                                </div>
                              </div>
                              <Badge
                                variant={user.is_active ? 'success' : 'secondary'}
                                appearance={user.is_active ? 'light' : undefined}
                                className="capitalize"
                              >
                                {user.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              {roles.length ? (
                                roles.map((role) => (
                                  <Badge key={`${user.id}-${role}`} variant="outline" className="capitalize">
                                    {role.replace(/_/g, ' ')}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground">Role not set</span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {user.location?.name || 'All Locations'}
                              </span>
                              {user.job_title && (
                                <span className="text-xs text-muted-foreground">{user.job_title}</span>
                              )}
                              {user.department && (
                                <span className="text-xs text-muted-foreground">{user.department}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <p className="text-muted-foreground">No tenant users found.</p>
                    </div>
                  )}

                  {tenantUsers?.meta?.last_page && tenantUsers.meta.last_page > 1 && (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-xs text-muted-foreground">
                        Page {tenantUsers.meta.current_page} of {tenantUsers.meta.last_page}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setUsersPage((prev) => Math.max(1, prev - 1))}
                          disabled={usersPage <= 1}
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setUsersPage((prev) => Math.min(tenantUsers.meta.last_page, prev + 1))
                          }
                          disabled={usersPage >= tenantUsers.meta.last_page}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="modules" className="space-y-6">
                  {/* Module Management */}
                  <div className="space-y-4">
                    <h3 className="ms-1 flex items-center gap-1.5 text-sm font-semibold">
                      <Package className="size-3.5 opacity-60" />
                      Available Modules
                    </h3>
                    
                    {modulesLoading ? (
                      <div className="flex flex-col items-center justify-center gap-2 py-12">
                        <Activity className="h-5 w-5 animate-spin text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Loading modules...</p>
                      </div>
                    ) : modulesError ? (
                      <div className="flex flex-col items-center justify-center gap-2 py-12">
                        <p className="text-sm text-destructive">Failed to load modules</p>
                        <p className="text-xs text-muted-foreground">Please try refreshing the page.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {allModules?.map((module) => {
                          const tenantModule = tenantModules?.find(
                            (tm) => tm.module_id === module.id
                          );
                          const isEnabled = tenantModule?.is_enabled || false;

                          return (
                            <div
                              key={module.id}
                              className="flex items-start justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                            >
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{module.display_name}</h4>
                                  {module.is_core && (
                                    <Badge variant="outline" className="text-xs">
                                      Core
                                    </Badge>
                                  )}
                                  {isEnabled && (
                                    <Badge variant="success" className="text-xs">
                                      Enabled
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {module.description}
                                </p>
                                {isEnabled && tenantModule?.enabled_at && (
                                  <p className="text-xs text-muted-foreground">
                                    Enabled on {new Date(tenantModule.enabled_at).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {!module.is_core && (
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isEnabled}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          enableModuleMutation.mutate({
                                            tenantId: tenantData.id,
                                            moduleId: module.id,
                                          });
                                        } else {
                                          if (confirm(`Are you sure you want to disable ${module.display_name}?`)) {
                                            disableModuleMutation.mutate({
                                              tenantId: tenantData.id,
                                              moduleId: module.id,
                                            });
                                          }
                                        }
                                      }}
                                      className="sr-only peer"
                                      disabled={enableModuleMutation.isPending || disableModuleMutation.isPending}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 dark:peer-focus:ring-primary/40 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                                  </label>
                                )}
                                {module.is_core && (
                                  <span className="text-xs text-muted-foreground">
                                    Always enabled
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="ai-keys" className="space-y-4">
                  <TenantAiKeyOverridesSection tenantId={tenantData.id} />
                </TabsContent>

                <TabsContent value="ai-config" className="space-y-4">
                  <TenantAiConfigSection tenantId={tenantData.id} />
                </TabsContent>

                <TabsContent value="ai-prompts" className="space-y-4">
                  <TenantAiPromptsSection tenantId={tenantData.id} />
                </TabsContent>

                <TabsContent value="billing" className="space-y-4">
                  <div className="py-12 text-center">
                    <p className="text-muted-foreground">Billing history coming soon...</p>
                  </div>
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </div>

        {/* Sidebar - Right Side */}
        <div className="w-[400px] shrink-0 border border-border rounded-lg">
          <Tabs defaultValue="details" className="text-sm">
            <TabsList
              variant="line"
              className="px-5 gap-6 bg-transparent [&_button]:border-b [&_button_svg]:size-3.5"
            >
              <TabsTrigger value="details">
                <Building2 />
                Details
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="w-full">
              <div className="px-5 py-4">
                <TabsContent value="details" className="space-y-4">
                  {/* Tenant Details Section */}
                  <Collapsible defaultOpen={true} className="space-y-2">
                    <div className="group flex items-center justify-between gap-2.5">
                      <CollapsibleTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-sm text-semibold [&:not(:hover)[data-state=open]]:bg-transparent hover:bg-accent ps-1.5 -ms-1.5"
                        >
                          <ChevronRight className="[[data-state=open]_&]:rotate-90" />
                          Tenant Details
                        </Button>
                      </CollapsibleTrigger>
                    </div>

                    <CollapsibleContent>
                      <div className="grid grid-cols-5 gap-2.5">
                        {/* Business Type */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <Building2 className="size-3.5 text-muted-foreground" />
                            Business Type
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-mono capitalize">{tenantData.business_type.replace('_', ' ')}</div>
                        </div>

                        {/* Email */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <Mail className="size-3.5 text-muted-foreground" />
                            Email
                          </div>
                        </div>
                        <div className="col-span-3">
                          <a href={`mailto:${tenantData.email}`} className="flex items-center gap-1.5 text-link hover:underline truncate">
                            {tenantData.email}
                          </a>
                        </div>

                        {/* Phone */}
                        {tenantData.phone && (
                          <>
                            <div className="col-span-2 pt-0.5">
                              <div className="text-muted-foreground flex items-center gap-1.5">
                                <Phone className="size-3.5 text-muted-foreground" />
                                Phone
                              </div>
                            </div>
                            <div className="col-span-3">
                              <div className="flex items-center text-mono">
                                {formatPhoneUS(tenantData.phone)}
                              </div>
                            </div>
                          </>
                        )}

                        {/* Website */}
                        {tenantData.website && (
                          <>
                            <div className="col-span-2 pt-0.5">
                              <div className="text-muted-foreground flex items-center gap-1.5">
                                <Globe className="size-3.5 text-muted-foreground" />
                                Website
                              </div>
                            </div>
                            <div className="col-span-3">
                              <div className="flex items-center gap-2">
                                <a
                                  href={tenantData.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary underline"
                                >
                                  {tenantData.website}
                                </a>
                                <ArrowUpRight className="size-3.5 text-muted-foreground" />
                              </div>
                            </div>
                          </>
                        )}

                        {/* Created */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <CalendarPlus className="size-3.5 text-muted-foreground" />
                            Created
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-mono">
                            {new Date(tenantData.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  {/* Subscription Section */}
                  <Collapsible defaultOpen={true} className="space-y-2">
                    <div className="group flex items-center justify-between gap-2.5">
                      <CollapsibleTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-sm text-semibold [&:not(:hover)[data-state=open]]:bg-transparent hover:bg-accent ps-1.5 -ms-1.5"
                        >
                          <ChevronRight className="[[data-state=open]_&]:rotate-90" />
                          Subscription
                        </Button>
                      </CollapsibleTrigger>
                    </div>

                    <CollapsibleContent>
                      <div className="grid grid-cols-5 gap-2.5">
                        {/* Plan */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <Package className="size-3.5 text-muted-foreground" />
                            Current Plan
                          </div>
                        </div>
                        <div className="col-span-3">
                          <Badge className="capitalize bg-purple-500/10 text-purple-700 border-purple-200">
                            {tenantData.subscription_plan}
                          </Badge>
                        </div>

                        {/* Status */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <Tag className="size-3.5 text-muted-foreground" />
                            Status
                          </div>
                        </div>
                        <div className="col-span-3">
                          {getStatusBadge(tenantData.status)}
                        </div>

                        {/* Billing */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <CreditCard className="size-3.5 text-muted-foreground" />
                            Billing
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-mono">Monthly</div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <Button variant="outline" size="sm" className="w-full">
                          Change Plan
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  {/* Additional Details - Collapsed by default */}
                  <Collapsible defaultOpen={false} className="space-y-2">
                    <CollapsibleContent>
                      <div className="grid grid-cols-5 gap-2.5 mb-2">
                        {/* Total Users */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <Users className="size-3.5 text-muted-foreground" />
                            Total Users
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-mono">-</div>
                        </div>

                        {/* Active Modules */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <Package className="size-3.5 text-muted-foreground" />
                            Active Modules
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-mono">-</div>
                        </div>

                        {/* Database */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <Activity className="size-3.5 text-muted-foreground" />
                            Database
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-mono text-xs truncate">{tenantData.database}</div>
                        </div>

                        {/* Updated At */}
                        <div className="col-span-2 pt-0.5">
                          <div className="text-muted-foreground flex items-center gap-1.5">
                            <CalendarPlus className="size-3.5 text-muted-foreground" />
                            Updated At
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="text-mono text-xs">
                            {new Date(tenantData.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>

                    <CollapsibleTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs p-1 h-auto text-semibold [&:not(:hover)[data-state=open]]:bg-transparent hover:bg-accent"
                      >
                        <ChevronRight className="[[data-state=open]_&]:-rotate-90" />
                        Show all details
                      </Button>
                    </CollapsibleTrigger>
                  </Collapsible>
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </div>
      </div>

      {/* Form Dialog */}
      <TenantFormSheet
        open={formDialog}
        onOpenChange={setFormDialog}
        tenant={tenantData}
      />
    </div>
  );
}
